<template>
  <!-- 审批中心：顶部统计 → 待办审批列表（分页）→ 点击某行在下方看它的流程流转记录 -->
  <div class="check-center">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="checkCenter">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="事项/单号/发起人" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.businessType" placeholder="全部" clearable @change="resetSearch">
            <template #prefix>业务类型</template>
            <el-option v-for="t in templateList" :key="t.businessType" :label="t.businessName" :value="t.businessType"/>
          </el-select>
          <el-select v-model="searchInfo.handled" placeholder="未处理" @change="resetSearch">
            <template #prefix>处理状态</template>
            <el-option label="未处理" :value="false"/>
            <el-option label="已处理" :value="true"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <!-- 切到「工作流」维度：那边看的是全商户的流程实例，不限于我的待办 -->
          <page-button @click="toWorkflowList">全部</page-button>
        </template>
      </search-condition>

      <!--
        ★ 这里原本多包了一层 <list-table>（现已删除）。ListPage 对直接子级 .list-table
          有 `flex: 1; height: 50%` 的默认规则，这层会被压成「剩余空间」，
          里面的待办表再叠一个 height="auto" 就必然溢出，表头下方冒出滚动条。
          删掉后各区块直接挂在 .list-page 下，由页面外壳统一滚动。
      -->
      <!-- 统计：四个数按当前登录人算，是「与我相关」的口径 -->
      <div class="stat-grid">
          <div class="stat-card">
            <div class="label">待我处理</div>
            <div class="value" style="color:#e6a23c">{{ stat.todoCount || 0 }}<span class="unit">条</span></div>
            <div class="sub">指派给我、尚未处理</div>
          </div>
          <div class="stat-card">
            <div class="label">我发起的</div>
            <div class="value" style="color:#409eff">{{ stat.startedCount || 0 }}<span class="unit">条</span></div>
            <div class="sub">仍在审批中</div>
          </div>
          <div class="stat-card">
            <div class="label">已通过</div>
            <div class="value" style="color:#67c23a">{{ stat.passedCount || 0 }}<span class="unit">条</span></div>
            <div class="sub">我发起且已完结</div>
          </div>
          <div class="stat-card">
            <div class="label">已驳回</div>
            <div class="value" style="color:#f56c6c">{{ stat.rejectedCount || 0 }}<span class="unit">条</span></div>
            <div class="sub">含被撤回的单据</div>
          </div>
        </div>

        <!-- 待办事项列表。点击任意一行从右侧滑出该单据的流转记录 -->
        <div class="section-title">待办事项</div>
        <list-table v-loading="loading" background-color="#FFF">
          <yq-table :data="dataList" :empty-text="'暂无待办审批'"
                    :highlight-current-row="true" row-key="id" :current-row-key="currentTaskId"
                    @rowClick="onRowClick">
            <yq-table-column label="审批事项" prop="bizTitle" min-width="220rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">
                <div class="cell-title">{{ row.bizTitle || '—' }}</div>
                <div v-if="row.processName" class="muted code-text">{{ row.processName }}</div>
              </template>
            </yq-table-column>
            <yq-table-column label="业务类型" prop="businessType" width="120rem" align="center">
              <template v-slot="{row}">
                <el-tag v-if="row.businessName" size="small" type="info">{{ row.businessName }}</el-tag>
                <span v-else>—</span>
              </template>
            </yq-table-column>
            <yq-table-column label="待办任务" prop="taskTypeName" width="100rem" align="center">
              <template v-slot="{row}">
                <el-tag size="small" :type="row.taskTypeName === '抄送' ? 'info' : 'warning'">
                  {{ row.taskTypeName || '审批' }}
                </el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="当前节点" prop="nodeName" width="140rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">
                <span>{{ row.nodeName || '—' }}</span>
                <el-tag v-if="row.checkType" size="small" type="warning" class="check-type-tag">
                  {{ checkTypeName(row.checkType) }}
                </el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="发起人" prop="startUserName" width="100rem">
              <template v-slot="{row}">{{ row.startUserName || '—' }}</template>
            </yq-table-column>
            <yq-table-column label="发起时间" prop="startTime" width="165rem">
              <template v-slot="{row}"><span class="num muted">{{ fmtTime(row.startTime) }}</span></template>
            </yq-table-column>
            <yq-table-column label="操作" prop="options" align="center" fixed="right" width="150"
                             :show-overflow-tooltip="false">
              <template v-slot="{row}">
                <!--
                  列表只给一个入口，不在这里选结果。
                  在列表上直接点「同意/驳回」等于先定结论再看单据，
                  顺序反了 —— 审批人必须先看到单据内容才能判断。
                  所以统一进审批详情页，看完明细后再决定。

                  已处理的任务只能看：那些操作对已完成的任务没有意义，
                  点了也只会报「任务已被处理」。
                -->
                <table-button v-if="isHandled(row)" @click.stop="toApprovalDetail(row)">查看</table-button>
                <table-button v-else-if="row.taskTypeName === '抄送'" el-type="primary"
                              @click.stop="toApprovalDetail(row)">确认</table-button>
                <table-button v-else el-type="primary"
                              @click.stop="toApprovalDetail(row)">审批</table-button>
              </template>
            </yq-table-column>
          </yq-table>
        </list-table>
        <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                   :total-count="searchInfo.totalCount" @onLoad="queryList"/>

    </list-page>

    <!--
      审批流转记录：点击待办行后从右侧滑出。
      放 drawer 而不是页面下方，是因为它在页面里会按内容撑开，
      把整页顶出第二条滚动条；抽屉有独立的滚动区域，不与列表抢。
      渲染复用 flow-log-panel，与业务单据页的「查看审批」是同一份实现。
    -->
    <el-drawer v-model="showFlowDrawer" :title="flowDrawerTitle" size="60%" :destroy-on-close="true">
      <div v-loading="flowLoading" class="flow-drawer">
        <flow-log-panel :detail="detail" empty-text="未找到该单据的流转记录"/>
      </div>
    </el-drawer>

    <!--
      审批不再用弹框，改跳「审批详情」页（ApprovalDetail）。
      单据字段多，弹框里审批人要不停滚动；而详情页同时是企业微信消息的落点，
      两处共用一个页面，就不存在「弹框里能批、消息里点开是另一套」的偏差。
    -->
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import FlowLogPanel from "./FlowLogPanel.vue";
import checkApi from "@/api/dz-project/project/checkApi";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";

/** 签署方式，与后端 ProcessFlowConstant.CheckType 对齐 */
const CHECK_TYPE = {1: "或签", 2: "会签", 3: "自动通过", 4: "并签"};
export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "CheckCenter",
  components: {
    FlowLogPanel, YqIcon, YqTableColumn, YqTable, TableButton, PageButton,
    FormPage, SearchCondition, ListTable, ListPage,
  },
  data() {
    return {
      searchInfo: {
        searchKey: "",
        businessType: "",
        // 默认只看未处理：进待办中心是来干活的，不是来翻历史的
        handled: false,
        pageNumber: 1,
        pageSize: 20,
        totalCount: 0,
      },
      stat: {},
      dataList: [],
      templateList: [],
      loading: false,
      // 当前选中的待办 id，用于表格高亮
      currentTaskId: "",
      // 当前选中待办对应的实例详情（含流转轨迹）
      detail: null,
      // 流转记录 drawer
      showFlowDrawer: false,
      flowLoading: false,
    };
  },
  computed: {
    /** 抽屉标题：带上事项标题，多开几次也能分清看的是哪一条 */
    flowDrawerTitle() {
      const title = this.detail && this.detail.bizTitle;
      return title ? "审批流转记录 · " + title : "审批流转记录";
    },
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.initTemplate();
    this.loadStat();
    this.queryList();
  },
  methods: {
    async initTemplate() {
      const list = await commonApi.queryBusinessTemplateList();
      this.templateList = list || [];
    },
    async loadStat() {
      const data = await checkApi.queryStat({businessType: this.searchInfo.businessType});
      this.stat = data || {};
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.currentTaskId = "";
      // 重新查询后原来选中那行多半已不在当前页，抽屉里的内容也就失效了，一并关掉
      this.detail = null;
      this.showFlowDrawer = false;
      this.loadStat();
      this.queryList();
    },
    /** 跳到工作流列表页：把当前的流程类型筛选带过去，避免用户再选一遍 */
    toWorkflowList() {
      this.router.push({
        path: "/project/view/check/workflow",
        query: {businessType: this.searchInfo.businessType || ""},
      });
    },
    async queryList() {
      this.loading = true;
      try {
        // 关键字在服务端只按业务类型过滤，文本检索交给前端做，
        // 避免为了模糊匹配在运行态表上再开一条索引
        const data = await checkApi.queryTodo({
          businessType: this.searchInfo.businessType,
          searchKey: this.searchInfo.searchKey,
          handled: this.searchInfo.handled,
          pageNumber: this.searchInfo.pageNumber,
          pageSize: this.searchInfo.pageSize,
        });
        // PageBean 的字段是 records / totalCount
        this.dataList = (data && data.records) || [];
        this.searchInfo.totalCount = (data && data.totalCount) || 0;
      } finally {
        this.loading = false;
      }
    },
    /** 点击某行：打开抽屉并加载该单据的完整流转记录 */
    async onRowClick(row) {
      this.currentTaskId = row.id;
      this.showFlowDrawer = true;
      this.detail = null;
      this.flowLoading = true;
      try {
        const detail = await checkApi.findInstanceDetail(row.instanceId);
        this.detail = detail || null;
      } catch (e) {
        this.detail = null;
      } finally {
        this.flowLoading = false;
      }
    },
    /**
     * 进审批详情页。
     *
     * 带 taskId 一起过去：同一实例上我可能既有历史任务、又有当前待办，
     * 只给 instanceId 的话详情页要靠猜，容易把「查看旧任务」点成「处理新任务」。
     * 操作模式不传 —— 由详情页按任务自身推导，避免同一件事两处判断、改漏一边。
     */
    toApprovalDetail(row) {
      this.router.push({
        path: "/project/view/check/detail",
        query: {instanceId: row.instanceId, taskId: row.id},
      });
    },
    /** 任务是否已处理完（已完成 / 已取消都算，都不该再提供操作） */
    isHandled(row) {
      // taskStatus：0待处理 100处理中 200已处理 300已取消
      return row.taskStatus === 200 || row.taskStatus === 300;
    },
    checkTypeName(checkType) {
      return CHECK_TYPE[checkType] || "";
    },
    fmtTime(v) {
      if (!v) {
        return "—";
      }
      return String(v).replace("T", " ").slice(0, 16);
    },
  },
};
</script>

<style lang="scss" scoped>
.check-center {
  height: 100%;
}

/*
  审批流转记录抽屉。
  ★ 自带 padding 与撑满高度：el-drawer 的 body 默认没有内边距，
    不补的话内容会贴着抽屉边缘，看着像没加载完。
*/
.flow-drawer {
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
  overflow: auto;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 14px 0 10px;

  .section-sub {
    font-size: 12px;
    font-weight: 400;
    color: #909399;
    margin-left: 8px;
  }
}

.instance-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: #606266;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 10px;

  .bar-item b {
    color: #303133;
    font-weight: 600;
  }
}

.empty-block {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #909399;
  font-size: 13px;
  background: #fafbfc;
  border: 1px dashed var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 20px 14px;
  justify-content: center;
}

.dialog-section {
  margin: 4px 0 10px;
}

.confirm-tip {
  font-size: 12px;
  line-height: 1.8;
  color: #909399;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 12px;

  b {
    color: #606266;
  }
}

.detail-wrap {
  min-height: 60px;
  margin-bottom: 4px;
}

.approve-summary {
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;

  .summary-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 6px;
  }

  .summary-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 12px;
    color: #909399;
  }
}

.check-type-tag {
  margin-left: 4px;
}

.num {
  font-variant-numeric: tabular-nums;
}

.muted {
  color: #909399;
}

.cell-title {
  color: #303133;
  font-weight: 500;
}

.code-text {
  font-family: Consolas, Menlo, monospace;
  font-size: 11px;
}
</style>
